import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';
import { type SsoChangeSummary } from './ssoEndpoints';
import { type TeamRole } from './teamEndpoints';

/** A team an account is in, with its role there. */
export interface UserTeam {
  id: string;
  name: string;
  role: TeamRole;
}

export interface User {
  id: string;
  name: string;
  username: string;
  /** How an account an admin defined is found the first time its owner signs in through the provider. */
  email?: string;
  role: 'sysadmin' | 'admin' | 'user';
  /** `sso`: the provider places the account. Absent from the team managers' short list. */
  managedBy?: 'local' | 'sso';
  /** Whether somebody has signed in to the account through the provider. */
  ssoLinked?: boolean;
  /** `false` while an admin has the account suspended. */
  isActive?: boolean;
  /** Single sign-on decides the account's role and teams; nobody changes them here. */
  ssoGoverned?: boolean;
  /** The sysadmin took the account off single sign-on, and it is not tied again until they allow it. */
  ssoLinkBlocked?: boolean;
  /** Whether the account has a password. In the admins' list only. */
  hasPassword?: boolean;
  /** The teams the account is in, with its role in each. In the admins' list only. */
  teams?: UserTeam[];
  /** When the account last signed in. Absent until its first sign-in since this was recorded. */
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** `password` may be left out only for an account that signs in through the provider. */
export type AddUserInput = Pick<User, 'name' | 'username' | 'role' | 'email'> & { password?: string };

/** The columns the admins' user list sorts by on the server, as its schema names them (`list-users.schema.ts`). */
export const userSortKeys = ['name', 'email', 'role', 'status', 'lastLoginAt', 'createdAt'] as const;

/** A page of the admins' user list, searched, sorted and cut on the server. */
export interface UserPageArgs {
  page: number;
  limit: number;
  search?: string;
  status?: 'active' | 'suspended';
  sort: string;
}

export interface UserPage {
  data: User[];
  meta: {
    /** The page these rows came from: the last one there is where one past it was asked for. */
    page: number;
    limit: number;
    total: number;
    pages: number;
    /** What the search leaves, whatever the status asked for: what the segments count. */
    counts: { all: number; active: number; suspended: number };
    /** Whether any account at all is suspended or has met single sign-on. */
    showsStatus: boolean;
  };
}

/** `email: null` takes the address away; `isActive: false` suspends the account. */
export type UpdateUserInput = Pick<User, 'id'> & {
  role?: 'admin' | 'user';
  name?: string;
  password?: string;
  email?: string | null;
  isActive?: boolean;
  /** `false` for one of many changes made together, whose page says once how they went. */
  showSuccess?: boolean;
};

/** An account as a transfer names it. */
export interface TransferAccount {
  id: string;
  username: string;
  name: string;
  role: User['role'];
  isActive: boolean;
}

/** A team of the account being deleted, and its role there. */
export interface TransferMembership {
  teamId: string;
  teamName: string;
  role: TeamRole;
}

/**
 * What an account being deleted holds that can be handed over, and once an account to hand it
 * to is named, what that account would get (`GET /v1/users/:id/transfer-preview`). `to`, `teams`
 * and `role` are `null` until then.
 */
export interface UserTransferPreview {
  from: TransferAccount;
  inspections: number;
  memberships: TransferMembership[];
  to: (TransferAccount & { ssoGoverned: boolean }) | null;
  teams: {
    /** `false` when single sign-on decides the teams of `to`. */
    transferable: boolean;
    /** Every membership as `to` would hold it: `current` its role there now, `result` after. */
    merge: (TransferMembership & { current: TeamRole | null; result: TeamRole })[];
  } | null;
  role: {
    current: User['role'];
    /** The deleted account's role where it outranks `to`'s; `null` when there is nothing to hand over. */
    offered: User['role'] | null;
    transferable: boolean;
  } | null;
}

/** What a delete hands over, and to whom. Left out, the account is deleted with nothing handed over. */
export interface UserTransferChoice {
  to: string;
  inspections: boolean;
  teams: boolean;
  role: boolean;
}

export const permissions = ['read', 'create', 'update', 'delete'] as const;

export type Permission = typeof permissions[number];

export interface ApiKey {
  _id: string;
  name: string;
  permissions: Permission[];
  createdAt: string;
  updatedAt: string;
}

export const userApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<User[], void>({
      query: () => '/v1/users',
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'User' as const, id })),
            { type: 'User', id: 'LIST' },
          ]
          : [{ type: 'User', id: 'LIST' }],
      onQueryStarted: handleApiResult(),

    }),
    // Every page is its own cache entry, all of them under the list's tag, which every change to
    // an account or a team's members invalidates.
    getUserPage: builder.query<UserPage, UserPageArgs>({
      query: (params) => ({ url: '/v1/users', params }),
      providesTags: [{ type: 'User', id: 'LIST' }],
      onQueryStarted: handleApiResult(),
    }),
    addUser: builder.mutation<void, AddUserInput>({
      query: (user) => ({
        url: '/v1/users',
        method: 'POST',
        body: user,
      }),
      invalidatesTags: ['User'],
      onQueryStarted: handleApiResult('Api_User_Add_Successful'),
    }),
    updateUser: builder.mutation<User, UpdateUserInput>({
      query: ({ id, showSuccess: _showSuccess, ...rest }) => ({
        url: `/v1/users/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      // The teams too: their members carry whether an account is suspended.
      // The pages of the list too: a suspended account leaves the Active one and changes its counts.
      invalidatesTags: (_result, _error, { id }) => [{ type: 'User' as const, id }, { type: 'User', id: 'LIST' }, 'Team'],
      onQueryStarted: (arg, api) =>
        handleApiResult(arg.showSuccess === false ? undefined : 'Api_User_Update_Successful')(arg, api),
    }),
    // The same request twice: first to show what would change, then to change it.
    previewSsoManagement: builder.mutation<SsoChangeSummary, { userIds: string[]; managedBy: 'sso' | 'local' }>({
      query: (body) => ({ url: '/v1/users/sso/management', method: 'POST', body: { ...body, dryRun: true } }),
      onQueryStarted: handleApiResult(),
    }),
    setSsoManagement: builder.mutation<SsoChangeSummary, { userIds: string[]; managedBy: 'sso' | 'local' }>({
      query: (body) => ({ url: '/v1/users/sso/management', method: 'POST', body }),
      // The teams too: handing an account over places it.
      invalidatesTags: ['User', 'Team'],
      onQueryStarted: handleApiResult('Api_User_Update_Successful'),
    }),
    unlinkSso: builder.mutation<void, { id: string; password?: string }>({
      query: ({ id, password }) => ({ url: `/v1/users/${id}/sso/unlink`, method: 'POST', body: password ? { password } : {} }),
      invalidatesTags: ['User', 'Team'],
      onQueryStarted: handleApiResult('Api_User_Update_Successful'),
    }),
    allowSsoLink: builder.mutation<void, string>({
      query: (id) => ({ url: `/v1/users/${id}/sso/allow-link`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [{ type: 'User' as const, id }, { type: 'User', id: 'LIST' }],
      onQueryStarted: handleApiResult('Api_User_Update_Successful'),
    }),
    /**
     * Asked again every time the delete dialog opens and every time another account is picked:
     * what an account holds moves with every upload and every team change.
     */
    getUserTransferPreview: builder.query<UserTransferPreview, { id: string; to?: string }>({
      query: ({ id, to }) => ({ url: `/v1/users/${id}/transfer-preview`, params: to ? { to } : undefined }),
      keepUnusedDataFor: 0,
      onQueryStarted: handleApiResult(),
    }),
    deleteUser: builder.mutation<void, { id: string; transfer?: UserTransferChoice; showSuccess?: boolean }>({
      query: ({ id, transfer }) => ({
        url: `/v1/users/${id}`,
        method: 'DELETE',
        // Only what was asked for: the API refuses a flag without `transferTo` and `transferTo` without a flag.
        params: transfer
          ? {
            transferTo: transfer.to,
            ...(transfer.inspections && { transferInspections: 'true' }),
            ...(transfer.teams && { transferTeams: 'true' }),
            ...(transfer.role && { transferRole: 'true' }),
          }
          : undefined,
      }),
      // The teams too: the server takes the account out of every team it was in. Handed over, the
      // account taking over changes too, and so do the authors every list of inspections names.
      invalidatesTags: (_result, _error, { id, transfer }) => [
        { type: 'User' as const, id },
        { type: 'User', id: 'LIST' },
        'Team',
        ...(transfer
          ? [{ type: 'User' as const, id: transfer.to }, { type: 'Inspection' as const, id: 'LIST' }, 'Statistic' as const]
          : []),
      ],
      onQueryStarted: (arg, api) =>
        handleApiResult(arg.showSuccess === false ? undefined : 'Api_User_Delete_Successful')(arg, api),
    }),
    getApiKeys: builder.query<ApiKey[], void>({
      query: () => '/v1/api-key',
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ _id: id }) => ({ type: 'ApiKey' as const, id })),
            { type: 'ApiKey', id: 'LIST' },
          ]
          : [{ type: 'ApiKey', id: 'LIST' }],
      onQueryStarted: handleApiResult(),
    }),
    addApiKey: builder.mutation<{ apiKey: string }, Omit<ApiKey, '_id' | 'createdAt' | 'updatedAt'>>({
      query: (apiKey) => ({
        url: '/v1/api-key',
        method: 'POST',
        body: apiKey,
      }),
      invalidatesTags: ['ApiKey'],
      onQueryStarted: handleApiResult('Api_ApiKey_Add_Successful'),
    }),
    updateApiKey: builder.mutation<void, Partial<ApiKey> & Pick<ApiKey, '_id'>>({
      query: ({ _id, ...rest }) => ({
        url: `/v1/api-key/${_id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_result, _error, { _id: id }) => [{ type: 'ApiKey', id }],
      onQueryStarted: handleApiResult('Api_ApiKey_Update_Successful'),
    }),
    deleteApiKey: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/api-key/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'ApiKey', id }],
      onQueryStarted: handleApiResult('Api_ApiKey_Delete_Successful'),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserPageQuery,
  useAddUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUserTransferPreviewQuery,
  usePreviewSsoManagementMutation,
  useSetSsoManagementMutation,
  useUnlinkSsoMutation,
  useAllowSsoLinkMutation,
  useGetApiKeysQuery,
  useAddApiKeyMutation,
  useUpdateApiKeyMutation,
  useDeleteApiKeyMutation,
} = userApiSlice;
